import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import TopBar from './components/TopBar';
import ProjectsPage from './pages/ProjectsPage';
import TimeEntry from './pages/TimeEntry';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Login from './pages/Login';
import { EntriesProvider } from './context/EntriesContext';
import { PeopleProvider } from './context/PeopleContext';
import RequesterPortal from './pages/RequesterPortal';
import PasswordSection from './components/settings/PasswordSection';
import { Toaster } from 'react-hot-toast';
import useTheme from './hooks/useTheme';
import { setUnauthorizedHandler, loadSession, saveSession, clearSession, markPasswordChanged } from './utils/session';

function App() {
  const { darkMode, setDarkMode } = useTheme();
  // Session (signed token) persists for its lifetime in localStorage.
  const [initialSession] = useState(loadSession);
  const [loggedInUser, setLoggedInUser] = useState(initialSession ? initialSession.username : '');

  const [isAdmin, setIsAdmin] = useState(Boolean(initialSession && initialSession.isAdmin));
  const [role, setRole] = useState(initialSession ? initialSession.role : '');
  const [mustChangePassword, setMustChangePassword] = useState(Boolean(initialSession && initialSession.mustChangePassword));

  const handleLogin = (username, user) => {
    saveSession(user); // must happen before any authenticated request is made
    setLoggedInUser(username);
    setIsAdmin(Boolean(user && (user.isAdmin || user.role === 'admin')));
    setRole(user ? user.role : '');
    setMustChangePassword(Boolean(user && user.mustChangePassword));
  };

  const handleLogout = () => {
    clearSession();
    setLoggedInUser('');
    setIsAdmin(false);
    setRole('');
    setMustChangePassword(false);
  };

  // A 401 on an authenticated call logs the user out (the token plumbing itself is installed in utils/session.js).
  useEffect(() => { setUnauthorizedHandler(handleLogout); }, []); // eslint-disable-line no-use-before-define

  // The production app can be served either at the domain root (Vercel
  // previews) or below /timecreative (the existing hosted installation).
  const routerBasename = window.location.pathname === '/timecreative' ||
    window.location.pathname.startsWith('/timecreative/')
    ? '/timecreative'
    : '/';

  return (
    <Router basename={routerBasename}>
      <Toaster position="top-right" />
      {!loggedInUser ? (
        <Login onLogin={handleLogin} />
      ) : mustChangePassword ? (
        // Accounts created or reset by an admin must choose their own password first.
        <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
          <div className="bg-surface rounded-tc-lg border border-line shadow p-6 w-full max-w-md text-ink">
            <h1 className="text-xl font-bold mb-2">Set a new password</h1>
            <p className="text-sm text-muted mb-4">
              You signed in with a temporary password. Enter it as your current password and choose your own.
            </p>
            <PasswordSection
              loggedInUser={loggedInUser}
              onChanged={() => { markPasswordChanged(); setMustChangePassword(false); }}
            />
            <button onClick={handleLogout} className="mt-4 text-sm underline text-muted">Log out</button>
          </div>
        </div>
      ) : role === 'requester' ? (
        // Requesters get the read-only portal and nothing else (the server enforces this as well).
        <RequesterPortal loggedInUser={loggedInUser} onLogout={handleLogout} />
      ) : (
        <EntriesProvider>
          <PeopleProvider username={loggedInUser}>
            <div className="flex min-h-screen flex-col">
              <TopBar darkMode={darkMode} setDarkMode={setDarkMode} onLogout={handleLogout} loggedInUser={loggedInUser} />
              <main className="flex-1 bg-canvas text-ink p-6">
                <Routes>
                  <Route path="/" element={<TimeEntry loggedInUser={loggedInUser} role={role} isAdmin={isAdmin} />} />
                  <Route path="/time-entry" element={<TimeEntry loggedInUser={loggedInUser} role={role} isAdmin={isAdmin} />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/projects" element={<ProjectsPage loggedInUser={loggedInUser} />} />
                  <Route path="/settings" element={<Settings loggedInUser={loggedInUser} isAdmin={isAdmin} />} />
                </Routes>
              </main>
            </div>
          </PeopleProvider>
        </EntriesProvider>
      )}
    </Router>
  );
}

export default App;
